import React from 'react';
import { CreditCard, ShoppingBag, Wallet, TrendingUp, AlertCircle, FileText } from 'lucide-react';

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const when = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export const CustomerTransactionSummary = ({ summary }) => {
  if (!summary) return null;
  const cards = [
    { label: 'Lifetime paid', value: money(summary.total_paid), icon: Wallet, tone: 'text-green-700 bg-green-50', testid: 'txn-summary-paid' },
    { label: 'Outstanding balance', value: money(summary.outstanding_balance), icon: AlertCircle, tone: summary.outstanding_balance > 0 ? 'text-red-700 bg-red-50' : 'text-neutral-600 bg-neutral-50', testid: 'txn-summary-outstanding' },
    { label: 'Total invoiced', value: money(summary.total_invoiced), icon: FileText, tone: 'text-blue-700 bg-blue-50', testid: 'txn-summary-invoiced' },
    { label: 'Total quoted', value: money(summary.total_quoted), icon: TrendingUp, tone: 'text-amber-700 bg-amber-50', testid: 'txn-summary-quoted' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="txn-summary">
      {cards.map(({ label, value, icon: Icon, tone, testid }) => (
        <div key={label} className="bg-white rounded-lg border border-neutral-200 p-4" data-testid={testid}>
          <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${tone}`}><Icon className="w-4 h-4" /></div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
          <p className="text-lg font-semibold text-neutral-900 font-mono">{value}</p>
        </div>
      ))}
      {summary.last_activity && (
        <p className="col-span-full text-xs text-neutral-400" data-testid="txn-summary-last-activity">Last activity {when(summary.last_activity)}</p>
      )}
    </div>
  );
};

const statusTone = (s) => (
  s === 'paid' ? 'bg-green-100 text-green-700' :
  s === 'pending' || s === 'unpaid' || s === 'initiated' || s === 'open' ? 'bg-amber-100 text-amber-700' :
  s === 'failed' || s === 'expired' || s === 'cancelled' ? 'bg-red-100 text-red-700' :
  'bg-neutral-100 text-neutral-700'
);

export const CustomerPaymentsSection = ({ payments = [] }) => (
  <div className="bg-white rounded-lg border border-neutral-200" data-testid="txn-payments-section">
    <div className="px-5 py-4 border-b border-neutral-200">
      <h3 className="font-medium flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-emerald-600" /> Payments
        <span className="text-neutral-400 text-sm">({payments.length})</span>
      </h3>
    </div>
    {payments.length === 0 ? (
      <p className="text-neutral-500 text-sm text-center py-6">No payments recorded</p>
    ) : (
      <div className="divide-y divide-neutral-100">
        {payments.map((p) => (
          <div key={`${p.source}-${p.id}`} className="px-5 py-3 flex items-center justify-between hover:bg-neutral-50" data-testid={`txn-payment-${p.id}`}>
            <div className="min-w-0">
              <p className="font-medium text-sm">{p.description}</p>
              <p className="text-xs text-neutral-500 truncate">
                {when(p.created_at)} · {p.source === 'stripe' ? 'Stripe' : (p.method || 'other').replace(/_/g, ' ')}
                {p.reference && <span className="font-mono"> · {String(p.reference).slice(0, 28)}</span>}
                {p.recorded_by && <span> · by {p.recorded_by}</span>}
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className={`px-2 py-1 rounded text-xs font-medium ${statusTone(p.status)}`}>{p.status}</span>
              <span className="font-medium font-mono">{money(p.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const CustomerOrdersSection = ({ orders = [] }) => (
  <div className="bg-white rounded-lg border border-neutral-200" data-testid="txn-orders-section">
    <div className="px-5 py-4 border-b border-neutral-200">
      <h3 className="font-medium flex items-center gap-2">
        <ShoppingBag className="w-4 h-4 text-indigo-600" /> Online Orders
        <span className="text-neutral-400 text-sm">({orders.length})</span>
      </h3>
    </div>
    {orders.length === 0 ? (
      <p className="text-neutral-500 text-sm text-center py-6">No online orders</p>
    ) : (
      <div className="divide-y divide-neutral-100">
        {orders.map((o) => (
          <div key={o.id} className="px-5 py-3 flex items-center justify-between hover:bg-neutral-50" data-testid={`txn-order-${o.id}`}>
            <div>
              <p className="font-medium text-sm">{o.order_number || `Order #${String(o.id).slice(0, 8)}`}</p>
              <p className="text-xs text-neutral-500">{when(o.created_at)} · {(o.items || []).length} item(s)</p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-2 py-1 rounded text-xs font-medium ${statusTone(o.status)}`}>{(o.status || 'pending').replace(/_/g, ' ')}</span>
              <span className="font-medium font-mono">{money(o.total || o.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
